import { DatePipe } from "@angular/common";
import { Component, computed, OnInit, signal } from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { PageEvent } from "@angular/material/paginator";
import { ActivatedRoute, Router } from "@angular/router";
import { paginatorConfig } from "@app/app.const";
import {
  DateRange,
  DateRangeFilterComponent,
} from "@components/filters/date-range-filter/date-range-filter.component";
import { FilterBarComponent } from "@components/filters/filter-bar/filter-bar.component";
import {
  FormAutocompleteComponent,
  FormAutocompleteOption,
} from "@components/filters/form-autocomplete/form-autocomplete.component";
import { RefreshButtonComponent } from "@components/refresh-button/refresh-button.component";
import { DataTableComponent } from "@components/table/data-table/data-table.component";
import { TableAction, TableColumn, TableConfig } from "@components/table/models/table-config.model";
import { RequestService } from "@services/request/request.service";
import {
  AuditLogDiffDialogComponent,
  AuditLogDiffDialogData,
} from "./audit-log-diff-dialog.component";

interface AuditLogRow {
  id: number;
  entity_table: string;
  entity_id: string | null;
  action: "INSERT" | "UPDATE" | "DELETE";
  old_values: Record<string, any> | null;
  new_values: Record<string, any> | null;
  changed_by_user_id: number | null;
  changed_by_type: "admin" | "vendor" | null;
  changed_by_email: string | null;
  request_route: string | null;
  created_at: string;
}

@Component({
  selector: "fh-change-log",
  templateUrl: "./change-log.component.html",
  styleUrls: ["./change-log.component.css"],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatDialogModule,
    FilterBarComponent,
    DataTableComponent,
    FormAutocompleteComponent,
    DateRangeFilterComponent,
    RefreshButtonComponent,
  ],
  providers: [DatePipe],
})
export class ChangeLogComponent implements OnInit {
  refreshData = (): void => this.getAuditLog();

  readonly actionOptions: FormAutocompleteOption<string>[] = [
    { value: "INSERT", label: "Created" },
    { value: "UPDATE", label: "Updated" },
    { value: "DELETE", label: "Deleted" },
  ];
  actionFilterFormControl = new FormControl<string | null>(null);

  readonly actorTypeOptions: FormAutocompleteOption<string>[] = [
    { value: "admin", label: "Admin" },
    { value: "vendor", label: "Vendor" },
  ];
  actorTypeFilterFormControl = new FormControl<string | null>(null);

  // A dense, high-row-count list — deliberately not the shared paginatorConfig
  // (10/25/50), which is tuned for lists with wider, taller rows.
  readonly pageSizeOptions = [20, 50, 100];
  readonly initialPageSize = 20;

  // State signals
  entityTable = signal<string>("");
  action = signal<string | null>(null);
  actorType = signal<string | null>(null);
  adminEmail = signal<string>("");
  dateFrom = signal<Date | null>(null);
  dateTo = signal<Date | null>(null);
  pageIndex = signal<number>(0);
  pageSize = signal<number>(this.initialPageSize);
  isLoading = signal<boolean>(false);

  // Data signals
  rows = signal<AuditLogRow[]>([]);
  total = signal<number>(0);

  readonly paginatorConfig = paginatorConfig;

  tableConfig = computed<TableConfig<AuditLogRow>>(() => ({
    columns: this.getTableColumns(),
    data: this.rows(),
    total: this.total(),
    loading: this.isLoading(),
    pageSizeOptions: this.pageSizeOptions,
    initialPageSize: this.initialPageSize,
    showFirstLastButtons: true,
    stickyHeader: true,
    rowClickable: false,
  }));

  tableActions = computed<TableAction<AuditLogRow>[]>(() => [
    {
      icon: "visibility",
      label: "View Diff",
      handler: (row) => this.viewDiff(row),
    },
  ]);

  constructor(
    private auth: RequestService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private datePipe: DatePipe,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.activatedRoute.queryParams.subscribe((params) => {
      this.entityTable.set(params.entityTable || "");
      this.action.set(params.action || null);
      this.actorType.set(params.actorType || null);
      this.adminEmail.set(params.adminEmail || "");
      this.dateFrom.set(params.dateFrom ? new Date(params.dateFrom) : null);
      this.dateTo.set(params.dateTo ? new Date(params.dateTo) : null);
      this.pageSize.set(+(params.pageSize || this.initialPageSize));
      this.pageIndex.set(+(params.page || 0));

      this.actionFilterFormControl.patchValue(this.action(), { emitEvent: false });
      this.actorTypeFilterFormControl.patchValue(this.actorType(), { emitEvent: false });

      this.getAuditLog();
    });
  }

  onEntityTableChange(value: string): void {
    this.updateQueryParams({ entityTable: value || undefined, page: 0 });
  }

  onActionChange(value: string | null): void {
    this.updateQueryParams({ action: value || undefined, page: 0 });
  }

  onActorTypeChange(value: string | null): void {
    this.updateQueryParams({ actorType: value || undefined, page: 0 });
  }

  onAdminEmailChange(value: string): void {
    this.updateQueryParams({ adminEmail: value || undefined, page: 0 });
  }

  onDateRangeChange(range: DateRange): void {
    this.updateQueryParams({
      dateFrom: range.start ? range.start.toISOString() : undefined,
      dateTo: range.end ? range.end.toISOString() : undefined,
      page: 0,
    });
  }

  onResetFilters(): void {
    this.actionFilterFormControl.patchValue(null, { emitEvent: false });
    this.actorTypeFilterFormControl.patchValue(null, { emitEvent: false });
    this.updateQueryParams({
      entityTable: undefined,
      action: undefined,
      actorType: undefined,
      adminEmail: undefined,
      dateFrom: undefined,
      dateTo: undefined,
      page: 0,
    });
  }

  onPageChange(event: PageEvent): void {
    this.updateQueryParams({
      page: event.pageIndex,
      pageSize: event.pageSize,
    });
  }

  viewDiff(row: AuditLogRow): void {
    this.dialog.open<AuditLogDiffDialogComponent, AuditLogDiffDialogData>(
      AuditLogDiffDialogComponent,
      {
        data: {
          entityTable: row.entity_table,
          entityId: row.entity_id,
          action: row.action,
          changedByEmail: row.changed_by_email,
          changedByType: row.changed_by_type,
          createdAt: row.created_at,
          oldValues: row.old_values,
          newValues: row.new_values,
        },
        width: "760px",
        maxWidth: "95vw",
        maxHeight: "90vh",
      }
    );
  }

  private getAuditLog(): void {
    const payload = {
      pageIndex: this.pageIndex(),
      pageSize: this.pageSize(),
      entityTable: this.entityTable() || undefined,
      action: this.action() || undefined,
      actorType: this.actorType() || undefined,
      adminEmail: this.adminEmail() || undefined,
      dateFrom: this.dateFrom() ? this.dateFrom()?.toISOString() : undefined,
      dateTo: this.dateTo() ? this.dateTo()?.toISOString() : undefined,
      sortDir: "desc",
    };

    this.isLoading.set(true);
    this.auth.post("/api/v2/audit-log/search", payload).subscribe({
      next: (res: any) => {
        if (res.result_code === 1) {
          this.total.set(res.data.totalCount || 0);
          this.rows.set(res.data?.list || []);
        }
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error("Error fetching audit log:", error);
        this.rows.set([]);
        this.total.set(0);
        this.isLoading.set(false);
      },
    });
  }

  private updateQueryParams(params: any): void {
    this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: params,
      queryParamsHandling: "merge",
    });
  }

  private getTableColumns(): TableColumn<AuditLogRow>[] {
    return [
      {
        // Column widths are set in the stylesheet, not here: the data table
        // turns `width` into max-width, which clips the cell rather than sizing
        // it - which is why Table, Action and Role were showing "order_det…",
        // "UP…" and "A…".
        key: "created_at",
        header: "When",
        sortable: false,
        type: "date",
        // Times are SGT throughout this page — see the note above the table,
        // so no per-row "SGT" suffix needed. yyMMdd HH:mm, e.g. 260915 11:36.
        format: (value) => this.datePipe.transform(value, "yyMMdd HH:mm", "+0800") ?? "—",
      },
      {
        key: "entity_table",
        header: "Table",
        sortable: false,
      },
      {
        key: "action",
        header: "Action",
        sortable: false,
      },
      {
        key: "changed_by_email",
        header: "Changed By",
        sortable: false,
        // Username only — the domain is always @fishyhub.com / the vendor's
        // own domain and just adds width without adding information here.
        format: (value) => (value ? value.split("@")[0] : "—"),
      },
      {
        key: "changed_by_type",
        header: "Role",
        sortable: false,
        format: (value) => (value ? (value === "admin" ? "Admin" : "Vendor") : "—"),
      },
    ];
  }
}
